/** Note names by pitch, from C, spelled with sharps. */
const SHARPS = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];

/** Note names by pitch, from C, spelled with flats. */
const FLATS = ["C", "Db", "D", "Eb", "E", "F", "Gb", "G", "Ab", "A", "Bb", "B"];

/** How musicians usually name each major key, by pitch. */
const MAJOR_KEYS = ["C", "Db", "D", "Eb", "E", "F", "F#", "G", "Ab", "A", "Bb", "B"];

/** How musicians usually name each minor key, by pitch. */
const MINOR_KEYS = ["C", "C#", "D", "Eb", "E", "F", "F#", "G", "G#", "A", "Bb", "B"];

/** Keys whose chords are written with flats. All others use sharps. */
const FLAT_KEYS = new Set(["F", "Bb", "Eb", "Ab", "Db", "Gb", "Cb", "Dm", "Gm", "Cm", "Fm", "Bbm", "Ebm", "Abm"]);

/**
 * A chord: a root note, what follows it (like `m7` or `sus4`), and maybe a bass note after a slash.
 * The rest can't hold another slash chord's bass, so `C6/9` reads as C with `6/9`.
 */
const CHORD = /^([A-G])([#b♯♭]?)(.*?)(?:\/([A-G])([#b♯♭]?))?$/;

/** The pitch of a note letter with its sharp or flat, from 0 for C to 11 for B. */
function pitch(letter: string, accidental: string): number {
    const natural = SHARPS.indexOf(letter);
    const shift = accidental === "#" || accidental === "♯" ? 1 : accidental === "b" || accidental === "♭" ? -1 : 0;

    return (natural + shift + 12) % 12;
}

function shifted(pitchClass: number, steps: number): number {
    return (((pitchClass + steps) % 12) + 12) % 12;
}

/** Whether chords in a key are written with flats, like in F or Bb. */
export function usesFlats(key: string | undefined): boolean {
    return key !== undefined && FLAT_KEYS.has(key);
}

/**
 * Transposes a chord by `steps` half steps, spelling notes with flats or sharps.
 * Anything that isn't a chord, like `N.C.` or `x2`, stays as it is.
 */
export function transposeChord(chord: string, steps: number, flats: boolean): string {
    const match = CHORD.exec(chord.trim());

    if (!match) return chord;

    const [, root, accidental, rest, bass, bassAccidental] = match;
    const names = flats ? FLATS : SHARPS;
    const moved = names[shifted(pitch(root, accidental), steps)] + rest;

    return bass ? `${moved}/${names[shifted(pitch(bass, bassAccidental), steps)]}` : moved;
}

/** A key moved by `steps` half steps, named the way musicians usually do, like G + 2 = A. Unreadable keys stay as they are. */
export function transposeKey(key: string, steps: number): string {
    const match = CHORD.exec(key.trim());

    if (!match) return key;

    const [, root, accidental, rest] = match;
    const minor = rest.startsWith("m") && !rest.startsWith("maj");
    const target = shifted(pitch(root, accidental), steps);

    return minor ? `${MINOR_KEYS[target]}m` : MAJOR_KEYS[target];
}

/** The key a chord suggests when a song doesn't say its key: its root, and minor if the chord is. */
export function keyOfChord(chord: string): string | undefined {
    const match = CHORD.exec(chord.trim());

    if (!match) return undefined;

    const [, root, accidental, rest] = match;
    const minor = rest.startsWith("m") && !rest.startsWith("maj");

    return `${root}${accidental.replace("♯", "#").replace("♭", "b")}${minor ? "m" : ""}`;
}

/** The steps of a major and a natural minor scale, in half steps from the root. */
const SCALES = { major: [0, 2, 4, 5, 7, 9, 11], minor: [0, 2, 3, 5, 7, 8, 10] };

/** A note to play in the bass, and whether it's in the song's key. */
export type BassNote = { note: string; inKey: boolean };

/** Whether something is a chord we can read, like `C` or `F#m7/A`, unlike `N.C.` or `x2`. */
export function isChord(chord: string): boolean {
    return CHORD.test(chord.trim());
}

/** The bass note of a slash chord, like `E` for `C/E`. */
export function bassOf(chord: string): string | undefined {
    const match = CHORD.exec(chord.trim());

    return match?.[4] ? match[4] + match[5] : undefined;
}

/** A chord with another bass note, or without one. A bass note that's the chord's own root is left out: C/C is C. */
export function withBass(chord: string, bass: string | undefined): string {
    const match = CHORD.exec(chord.trim());

    if (!match) return chord;

    const [, root, accidental, rest] = match;
    const bassMatch = bass === undefined ? undefined : CHORD.exec(bass);
    const same = bassMatch && pitch(bassMatch[1], bassMatch[2]) === pitch(root, accidental);

    return bass === undefined || same ? root + accidental + rest : `${root}${accidental}${rest}/${bass}`;
}

/**
 * All twelve notes to choose a bass note from, spelled to suit the key: first the notes of the key's scale from its
 * root, then the others.
 */
export function bassNotes(key: string): BassNote[] {
    const match = CHORD.exec(key.trim());
    const root = match ? pitch(match[1], match[2]) : 0;
    const minor = !!match && match[3].startsWith("m") && !match[3].startsWith("maj");
    const names = usesFlats(key) ? FLATS : SHARPS;
    const scale = SCALES[minor ? "minor" : "major"];
    const others = [...Array(12).keys()].filter((step) => !scale.includes(step));

    return [
        ...scale.map((step) => ({ note: names[shifted(root, step)], inKey: true })),
        ...others.map((step) => ({ note: names[shifted(root, step)], inKey: false })),
    ];
}
